import { useDeferredValue, useMemo } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { Product } from "@/types";

import { mapOpenSearchSourceToProduct } from "../mappers/map-opensearch-source-to-product";
import { ProductSearchService } from "../services/product-search.service";
import { buildOrderProductSearchBody } from "../utils/build-order-product-search-body";
import type { OpenSearchStoreReadiness } from "../utils/get-opensearch-store-readiness";
import { mapProductSearchHits } from "../utils/map-product-search-hits";
import {
    OPENSEARCH_PRODUCTS_STALE_MS,
    ORDER_PRODUCT_SEARCH_SIZE,
} from "../utils/product-search-constants";
import type { OpenSearchStore } from "../types/products";

import { useOpenSearchStoreReadiness } from "./useOpenSearchStoreReadiness";

type UseOrderProductSearchOptions = {
    providerStoreID?: string;
    searchText: string;
    showOutOfStock: boolean;
    enabled?: boolean;
};

type UseOrderProductSearchResult = {
    products: Product[];
    isSearching: boolean;
    openSearchStore: OpenSearchStore | null;
    isLoading: boolean;
    readiness: OpenSearchStoreReadiness | null;
    isReady: boolean;
};

function useOrderProductSearch({
    providerStoreID,
    searchText,
    showOutOfStock,
    enabled = true,
}: UseOrderProductSearchOptions): UseOrderProductSearchResult {
    const storeId = providerStoreID?.trim() ?? "";
    const deferredSearchText = useDeferredValue(searchText.trim());
    const { isLoading, readiness, isReady } =
        useOpenSearchStoreReadiness(storeId);

    const openSearchStore =
        readiness?.status === "ready" ? readiness.store : null;

    const searchService = useMemo(() => {
        if (!openSearchStore) {
            return null;
        }

        return new ProductSearchService(
            openSearchStore.mso_store_entity_id,
            openSearchStore.mso_org_id,
        );
    }, [openSearchStore]);

    const canSearch = Boolean(storeId) && enabled && isReady;

    const searchBody = useMemo(
        () =>
            buildOrderProductSearchBody({
                searchText: deferredSearchText,
                showOutOfStock,
                size: ORDER_PRODUCT_SEARCH_SIZE,
            }),
        [deferredSearchText, showOutOfStock],
    );

    const { data, isFetching } = useQuery({
        queryKey: [
            "order-product-search",
            openSearchStore?.short_name ?? "",
            openSearchStore?.mso_store_entity_id ?? "",
            deferredSearchText,
            showOutOfStock,
        ],
        queryFn: async () => {
            if (!searchService) {
                return [];
            }

            const response = await searchService.search({ searchBody });
            return mapProductSearchHits(
                response.hits?.hits,
                mapOpenSearchSourceToProduct,
            );
        },
        enabled: Boolean(searchService && canSearch),
        placeholderData: keepPreviousData,
        staleTime: OPENSEARCH_PRODUCTS_STALE_MS,
    });

    return {
        products: canSearch ? data ?? [] : [],
        isSearching: isFetching && canSearch,
        openSearchStore,
        isLoading,
        readiness,
        isReady,
    };
}

export { useOrderProductSearch };
export type { UseOrderProductSearchResult };
